import { makeAutoObservable, observable, runInAction } from "mobx";
import type { RootStore } from "./root.store";
import type { TMemberAvailability } from "@/types/availability";
import { getDefaultDateRange } from "@/components/availability/date-helpers";

const MAX_RANGE_DAYS = 90;

function isValidIsoDate(value: string): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(new Date(`${value}T00:00:00`).getTime())
  );
}

function diffDays(start: string, end: string): number {
  const s = new Date(`${start}T00:00:00`).getTime();
  const e = new Date(`${end}T00:00:00`).getTime();
  return Math.floor((e - s) / 86_400_000);
}

export class WorkspaceAvailabilityStore {
  dataBySlug = observable.map<string, TMemberAvailability>();
  startDate: string;
  endDate: string;
  selectedSkillIds: Set<string> = new Set();
  isLoading = false;
  lastFetchKey: string | null = null;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    const defaults = getDefaultDateRange(13);
    this.startDate = defaults.start;
    this.endDate = defaults.end;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  get members(): TMemberAvailability {
    const slug = this.rootStore.router.workspaceSlug ?? "";
    return this.dataBySlug.get(slug) ?? {};
  }

  get visibleMembers(): TMemberAvailability {
    if (this.selectedSkillIds.size === 0) return this.members;
    return Object.fromEntries(
      Object.entries(this.members).filter(([, member]) =>
        (member.skills ?? []).some((id) => this.selectedSkillIds.has(id)),
      ),
    );
  }

  getMembersForSlug(slug: string): TMemberAvailability {
    return this.dataBySlug.get(slug) ?? {};
  }

  setDateRange(start: string, end: string): { ok: boolean; error?: string } {
    if (!isValidIsoDate(start) || !isValidIsoDate(end)) {
      return { ok: false, error: "invalid_date" };
    }
    const days = diffDays(start, end);
    if (days < 0) return { ok: false, error: "end_before_start" };
    if (days > MAX_RANGE_DAYS) return { ok: false, error: "range_too_long" };
    if (this.startDate !== start || this.endDate !== end) {
      const slug = this.rootStore.router.workspaceSlug;
      if (slug) this.dataBySlug.delete(slug);
    }
    this.startDate = start;
    this.endDate = end;
    return { ok: true };
  }

  toggleSkillFilter(skillId: string): void {
    if (this.selectedSkillIds.has(skillId)) this.selectedSkillIds.delete(skillId);
    else this.selectedSkillIds.add(skillId);
    this.selectedSkillIds = new Set(this.selectedSkillIds);
  }

  clearSkillFilter(): void {
    this.selectedSkillIds = new Set();
  }

  async fetchAvailability(slug: string): Promise<TMemberAvailability> {
    const key = `${slug}|${this.startDate}|${this.endDate}`;
    this.isLoading = true;
    try {
      const data = await this.rootStore.workspaceAvailabilityService.getTeamAvailability(
        slug,
        this.startDate,
        this.endDate,
      );
      runInAction(() => {
        this.dataBySlug.set(slug, data);
        this.lastFetchKey = key;
      });
      return data;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }
}
